import i18n from '../i18n';

const LOCALE_TAGS: Record<string, string> = { uz: 'uz-Latn-UZ', ru: 'ru-RU', en: 'en-GB' };
const tag = () => LOCALE_TAGS[i18n.language] ?? 'uz-Latn-UZ';
/**
 * Uzbek is formatted by hand: many browsers ship without Uzbek locale data and
 * would print "M10 6" or "UZS 12,500,000". Russian and English use Intl.
 */
const isUz = () => (i18n.language ?? 'uz').startsWith('uz');

let timeZone = 'Asia/Tashkent';
export function setTimeZone(tz: string) { timeZone = tz; }

const UZ_MONTHS_SHORT = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek'];
const UZ_MONTHS_LONG = ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'];
const UZ_WEEKDAYS = ['Yak', 'Dush', 'Sesh', 'Chor', 'Pay', 'Jum', 'Shan'];
const UZ_CURRENCY: Record<string, string> = { UZS: 'soʻm', USD: '$', EUR: '€' };

/** Groups digits with spaces: 12500000 → "12 500 000". */
function groupDigits(value: number, fractionDigits = 0) {
  const [int, frac] = Math.abs(value).toFixed(fractionDigits).split('.');
  const grouped = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${value < 0 ? '−' : ''}${grouped}${frac && Number(frac) ? `,${frac}` : ''}`;
}

function uzCompact(value: number) {
  const abs = Math.abs(value);
  const fmt = (n: number) => groupDigits(Math.round(n * 10) / 10, n < 10 && n % 1 ? 1 : 0);
  if (abs >= 1e9) return `${fmt(value / 1e9)} mlrd`;
  if (abs >= 1e6) return `${fmt(value / 1e6)} mln`;
  if (abs >= 1e3) return `${fmt(value / 1e3)} ming`;
  return groupDigits(value);
}

export function formatMoney(value: number | null | undefined, currency = 'UZS', opts: { compact?: boolean } = {}) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (isUz()) {
    const amount = opts.compact ? uzCompact(n) : groupDigits(n, currency === 'UZS' ? 0 : 2);
    return currency === 'UZS' ? `${amount} ${UZ_CURRENCY.UZS}` : `${UZ_CURRENCY[currency] ?? currency}${amount}`;
  }
  try {
    return new Intl.NumberFormat(tag(), {
      style: 'currency', currency, maximumFractionDigits: currency === 'UZS' ? 0 : 2,
      notation: opts.compact ? 'compact' : 'standard',
    }).format(n);
  } catch {
    return `${n.toLocaleString()} ${currency}`;
  }
}

export function formatNumber(value: number | null | undefined, opts: Intl.NumberFormatOptions = {}) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (isUz()) return groupDigits(n, Math.min(opts.maximumFractionDigits ?? (n % 1 ? 1 : 0), 2));
  return new Intl.NumberFormat(tag(), opts).format(n);
}

/** Calendar parts of a date in the organisation's time zone. */
function parts(d: Date) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
  }).formatToParts(d).map((x) => [x.type, x.value]));
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday ?? 'Sun');
  return { year: p.year!, month: Number(p.month) - 1, day: Number(p.day), time: `${p.hour}:${p.minute}`, weekday };
}

function formatUzDate(d: Date, opts: Intl.DateTimeFormatOptions) {
  const p = parts(d);
  const out: string[] = [];
  if (opts.weekday && !opts.day && !opts.month) return UZ_WEEKDAYS[p.weekday]!;
  if (opts.dateStyle) out.push(`${p.day}-${UZ_MONTHS_SHORT[p.month]}, ${p.year}`);
  else if (opts.month === 'long' && !opts.day) out.push(`${UZ_MONTHS_LONG[p.month]} ${opts.year ? p.year : ''}`.trim());
  else if (opts.day || opts.month) out.push(`${opts.day ? `${p.day}-` : ''}${opts.month === 'long' ? UZ_MONTHS_LONG[p.month] : UZ_MONTHS_SHORT[p.month]}${opts.year ? `, ${p.year}` : ''}`);
  if (opts.timeStyle || opts.hour) out.push(p.time);
  return out.join(', ') || `${p.day}-${UZ_MONTHS_SHORT[p.month]}, ${p.year}`;
}

export function formatDate(value: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value.length === 10 ? `${value}T12:00:00Z` : value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  if (isUz()) return formatUzDate(d, opts);
  return new Intl.DateTimeFormat(tag(), { timeZone, ...opts }).format(d);
}

export function formatDateTime(value: string | Date | null | undefined) {
  return formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });
}

function uzRelative(amount: number, unit: 'second' | 'minute' | 'hour' | 'day') {
  if (unit === 'second' || amount === 0) return 'hozir';
  if (unit === 'day' && amount === -1) return 'kecha';
  if (unit === 'day' && amount === 1) return 'ertaga';
  const word = { minute: 'daqiqa', hour: 'soat', day: 'kun' }[unit];
  return amount < 0 ? `${-amount} ${word} oldin` : `${amount} ${word}dan keyin`;
}

export function relativeTime(value: string | Date | null | undefined) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const [amount, unit] = abs < 60 ? [Math.round(diff), 'second' as const]
    : abs < 3600 ? [Math.round(diff / 60), 'minute' as const]
      : abs < 86400 ? [Math.round(diff / 3600), 'hour' as const]
        : abs < 86400 * 30 ? [Math.round(diff / 86400), 'day' as const] : [null, null];
  if (amount === null || unit === null) return formatDate(d);
  if (isUz()) return uzRelative(amount, unit);
  return new Intl.RelativeTimeFormat(tag(), { numeric: 'auto' }).format(amount, unit);
}

export function initials(name: string | null | undefined) {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
}

/** Formats a raw minute count as "2 h 5 min". */
export function formatMinutes(minutes: number | null | undefined) {
  if (minutes === null || minutes === undefined) return '—';
  const m = Math.round(Number(minutes));
  if (m < 60) return `${m} ${i18n.t('common.minutesShort')}`;
  return `${Math.floor(m / 60)} ${i18n.t('common.hoursShort')} ${m % 60} ${i18n.t('common.minutesShort')}`;
}
